import { useEffect, useMemo, useState } from "react";
import { Clear, Search } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import api from "../../../services/api";
import { hasSchema } from "../../../utils/dashboardSchemaDraft";
import {
    filterFabricantes,
    idsDosModelos,
} from "../../../utils/predefinicoesTree";
import ModeloCheckTree from "./modeloCheckTree";

// aplica o schema atual a vários modelos: escolher destinos e modo, prévia
// via dryRun, então confirmar. Merge e versionamento ficam no backend.
export default function SchemaBulkApply({
    open,
    onClose,
    fabricantes,
    sourceModeloId,
    sourceSchema,
    schemaNaoSalvo = false,
    nomeModelo,
    notify,
    onAplicado,
    styles,
}) {
    const [filtro, setFiltro] = useState("");
    const [expandidos, setExpandidos] = useState([]);
    const [selecionados, setSelecionados] = useState(() => new Set());
    const [modo, setModo] = useState("mesclar");
    const [previa, setPrevia] = useState(null);
    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        if (!open) return;
        setFiltro("");
        setSelecionados(new Set());
        setModo("mesclar");
        setPrevia(null);
        setErro(null);
        setExpandidos([]);
    }, [open]);

    const arvoreFiltrada = useMemo(
        () => filterFabricantes(fabricantes, filtro),
        [fabricantes, filtro],
    );

    // vem da árvore completa, não da filtrada, pra seleção escondida pela busca continuar contada
    const indiceModelos = useMemo(() => {
        const mapa = new Map();
        for (const fab of fabricantes || []) {
            if (!fab || fab.id == null) continue;
            for (const fun of fab.Funcoes || []) {
                if (!fun || fun.id == null) continue;
                for (const mod of fun.Modelos || []) {
                    if (mod && mod.id != null) mapa.set(mod.id, mod);
                }
            }
        }
        return mapa;
    }, [fabricantes]);

    const idsSelecionados = useMemo(
        () => [...selecionados].filter((id) => id !== sourceModeloId),
        [selecionados, sourceModeloId],
    );

    const jaComSchema = useMemo(
        () =>
            idsSelecionados.filter((id) => hasSchema(indiceModelos.get(id))).length,
        [idsSelecionados, indiceModelos],
    );

    const toggleExpand = (id) =>
        setExpandidos((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );

    const toggleMuitos = (ids, marcar) => {
        setPrevia(null);
        setSelecionados((prev) => {
            const proxima = new Set(prev);
            for (const id of ids) {
                if (marcar) proxima.add(id);
                else proxima.delete(id);
            }
            return proxima;
        });
    };

    const selecionarVisiveis = () =>
        toggleMuitos(idsDosModelos(arvoreFiltrada), true);

    async function chamar(dryRun) {
        setOcupado(true);
        setErro(null);
        try {
            const response = await api.put("/putSchemaModelos", {
                sourceModeloId,
                schema: sourceSchema,
                modeloIds: idsSelecionados,
                modo,
                dryRun,
            });
            return response?.data || null;
        } catch (e) {
            const dados = e?.response?.data;
            setErro({
                mensagem:
                    dados?.error || "Não foi possível concluir a operação no servidor.",
                detalhes: dados?.detalhes || [],
            });
            return null;
        } finally {
            setOcupado(false);
        }
    }

    const gerarPrevia = async () => {
        const dados = await chamar(true);
        if (dados) setPrevia(dados);
    };

    const aplicar = async () => {
        const dados = await chamar(false);
        if (!dados) return;

        notify?.({
            children: `Schema aplicado: ${dados.aplicados} alterado(s), ${dados.inalterados} sem mudança`,
            severity: "success",
        });
        onAplicado?.(dados);
        onClose();
    };

    const itensAlterados = (previa?.itens || []).filter(
        (item) => item.status === "aplicado",
    );
    const temRemocoes = itensAlterados.some(
        (item) =>
            (item.abasRemovidas || []).length > 0 ||
            (item.slotsRemovidos || []).length > 0,
    );
    const duplicatas = itensAlterados.flatMap(
        (item) => item.possiveisDuplicatas || [],
    );

    return (
        <Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Aplicar schema de <strong>{nomeModelo}</strong> a outros modelos
            </DialogTitle>

            <DialogContent dividers>
                <Stack direction="column" gap={2}>
                    {schemaNaoSalvo && (
                        <Alert severity="warning">
                            O schema em edição ainda não foi salvo em{" "}
                            <strong>{nomeModelo}</strong>. Os modelos escolhidos aqui vão
                            receber o conteúdo que está na tela; a origem continua com o
                            schema antigo até você salvar.
                        </Alert>
                    )}

                    <RadioGroup
                        row
                        value={modo}
                        onChange={(e) => {
                            setModo(e.target.value);
                            setPrevia(null);
                        }}
                    >
                        <FormControlLabel
                            value="mesclar"
                            control={<Radio size="small" />}
                            label="Mesclar"
                        />
                        <FormControlLabel
                            value="substituir"
                            control={<Radio size="small" />}
                            label="Substituir"
                        />
                    </RadioGroup>

                    <Alert severity={modo === "substituir" ? "warning" : "info"}>
                        {modo === "mesclar"
                            ? "Mesclar nunca remove nada do modelo de destino: abas e métricas que só existem lá continuam intactas, e itens repetidos não são duplicados."
                            : "Substituir troca o schema do destino por este. Abas e métricas que só existem no destino serão perdidas."}
                    </Alert>

                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Buscar fabricante, função ou modelo"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: filtro ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFiltro("")}>
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    <ModeloCheckTree
                        fabricantes={arvoreFiltrada}
                        selecionados={selecionados}
                        onToggleMuitos={toggleMuitos}
                        expandidos={expandidos}
                        onToggleExpand={toggleExpand}
                        excluirModeloId={sourceModeloId}
                        styles={styles}
                    />

                    {/* Contador persistente: seleção escondida pela busca nunca fica
                        invisível. */}
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                            size="small"
                            color={idsSelecionados.length > 0 ? "primary" : "default"}
                            label={`${idsSelecionados.length} modelo(s) selecionado(s)`}
                        />
                        {jaComSchema > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {jaComSchema}{" "}
                                {modo === "substituir"
                                    ? "já possui schema e será sobrescrito"
                                    : "já possui schema e será mesclado"}
                            </Typography>
                        )}
                        <Box flex={1} />
                        <Button size="small" onClick={selecionarVisiveis}>
                            selecionar visíveis
                        </Button>
                        <Button
                            size="small"
                            disabled={selecionados.size === 0}
                            onClick={() => {
                                setSelecionados(new Set());
                                setPrevia(null);
                            }}
                        >
                            limpar seleção
                        </Button>
                    </Stack>

                    {erro && (
                        <Alert severity="error">
                            {erro.mensagem}
                            {erro.detalhes.length > 0 && (
                                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                                    {erro.detalhes.map((detalhe) => (
                                        <li key={detalhe}>
                                            <Typography variant="caption">{detalhe}</Typography>
                                        </li>
                                    ))}
                                </Box>
                            )}
                        </Alert>
                    )}

                    {previa && (
                        <>
                            <Divider />
                            <Typography variant="mySubtitle2" color="primary">
                                Prévia
                            </Typography>
                            <Typography variant="body2">
                                {previa.total} selecionado(s) · {previa.aplicados} serão
                                alterados · {previa.inalterados} sem mudança
                                {previa.ignorados > 0
                                    ? ` · ${previa.ignorados} ignorado(s)`
                                    : ""}
                            </Typography>

                            {previa.aplicados === 0 && (
                                <Alert severity="info">
                                    Nada mudaria: os modelos selecionados já têm exatamente
                                    este conteúdo.
                                </Alert>
                            )}

                            {temRemocoes && (
                                <Alert severity="warning">
                                    Esta operação remove abas ou métricas que existem hoje nos
                                    destinos. Confira a lista abaixo antes de confirmar.
                                </Alert>
                            )}

                            {duplicatas.length > 0 && (
                                <Alert severity="warning">
                                    Há nomes de item que diferem só por acento ou maiúscula
                                    dentro do mesmo slot. Eles são mantidos separados, porque
                                    o app do cliente compara os nomes exatamente — vale
                                    conferir se algum está errado.
                                    <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                                        {duplicatas.slice(0, 5).map((dup, index) => (
                                            <li key={`${dup.caminho}-${index}`}>
                                                <Typography variant="caption">
                                                    {dup.caminho}: {dup.valores.join(" / ")}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            <Box sx={styles.bulkPrevia}>
                                {itensAlterados.map((item) => (
                                    <Box key={item.modeloId} sx={styles.bulkPreviaItem}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {item.modelo}{" "}
                                            <Typography component="span" variant="caption" color="text.disabled">
                                                v{item.versaoAnterior} → v{item.versaoNova}
                                            </Typography>
                                        </Typography>
                                        <LinhaDiff
                                            rotulo="abas +"
                                            valores={item.abasAdicionadas}
                                            cor="success.main"
                                        />
                                        <LinhaDiff
                                            rotulo="abas −"
                                            valores={item.abasRemovidas}
                                            cor="error.main"
                                        />
                                        <LinhaDiff
                                            rotulo="métricas +"
                                            valores={item.slotsAdicionados}
                                            cor="success.main"
                                        />
                                        <LinhaDiff
                                            rotulo="métricas ~"
                                            valores={item.slotsAlterados}
                                            cor="warning.main"
                                        />
                                        <LinhaDiff
                                            rotulo="métricas −"
                                            valores={item.slotsRemovidos}
                                            cor="error.main"
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={ocupado}>
                    Cancelar
                </Button>

                {!previa ? (
                    <Button
                        variant="contained"
                        onClick={gerarPrevia}
                        disabled={ocupado || idsSelecionados.length === 0}
                    >
                        {ocupado ? <CircularProgress size={20} /> : "Ver prévia"}
                    </Button>
                ) : (
                    <>
                        <Button onClick={() => setPrevia(null)} disabled={ocupado}>
                            Voltar
                        </Button>
                        <Button
                            variant="contained"
                            color={modo === "substituir" ? "warning" : "primary"}
                            onClick={aplicar}
                            disabled={ocupado || previa.aplicados === 0}
                        >
                            {ocupado ? (
                                <CircularProgress size={20} />
                            ) : (
                                `Aplicar em ${previa.aplicados} modelo(s)`
                            )}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

function LinhaDiff({ rotulo, valores, cor }) {
    if (!valores || valores.length === 0) return null;
    return (
        <Typography variant="caption" display="block" sx={{ color: cor }}>
            {rotulo} {valores.join(", ")}
        </Typography>
    );
}
